import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { useForm } from 'react-hook-form';
import { 
  Building2, 
  Edit, 
  Trash2, 
  Plus, 
  DollarSign, 
  TrendingUp, 
  Shield, 
  PiggyBank,
  CreditCard,
  Wallet,
  Target,
  PieChart,
  Building
} from 'lucide-react';
import ValidatedInput from '../components/ValidatedInput';
import ValidatedSelect from '../components/ValidatedSelect';
import ValidatedTextarea from '../components/ValidatedTextarea';
import { validationRules } from '../utils/validation';
import Disclaimer from '../components/Disclaimer';
import '../components/ValidatedInput.css';

const AccountManager = () => {
  const [accounts, setAccounts] = useState([]);
  const [showAddAccount, setShowAddAccount] = useState(false);
  const [showEditAccount, setShowEditAccount] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  // Form hook for add account form
  const { register: registerAccount, handleSubmit: handleSubmitAccount, formState: { errors: accountErrors }, reset: resetAccount } = useForm({
    defaultValues: {
      name: '',
      type: 'checking',
      institution: '',
      account_number: '',
      balance: '',
      interest_rate: '',
      tax_status: 'taxable',
      notes: '',
      status: 'active'
    }
  });

  // Form hook for edit account form
  const { register: registerEditAccount, handleSubmit: handleSubmitEditAccount, formState: { errors: editAccountErrors }, reset: resetEditAccount } = useForm();

  const [newAccount, setNewAccount] = useState({
    name: '',
    type: 'checking',
    institution: '',
    account_number: '',
    balance: '',
    interest_rate: '',
    tax_status: 'taxable',
    notes: '',
    status: 'active'
  });

  useEffect(() => {
    fetchAccounts();
  }, []);

  const fetchAccounts = async () => {
    try {
      console.log('🔍 Fetching accounts...');
      const token = localStorage.getItem('accessToken');
      console.log('🔍 Token:', token ? 'Present' : 'Missing');
      
      const response = await axios.get('/api/accounts', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      console.log('🔍 Accounts response:', response.data);
      console.log('🔍 Accounts array:', response.data.data);
      
      setAccounts(response.data.data || []);
      console.log('🔍 Accounts state updated:', response.data.data || []);
    } catch (error) {
      console.error('Error fetching accounts:', error);
      toast.error('Failed to fetch accounts');
      setAccounts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddAccount = async (data) => {
    try {
      const token = localStorage.getItem('accessToken');
      const accountData = {
        ...data,
        balance: data.balance ? parseFloat(data.balance) : 0,
        interest_rate: data.interest_rate ? parseFloat(data.interest_rate) : null,
        account_number: data.account_number || null
      };

      const response = await axios.post('/api/accounts', accountData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data.success) {
        toast.success('Account added successfully!');
        setShowAddAccount(false);
        resetAccount();
        setNewAccount({
          name: '',
          type: 'checking',
          institution: '',
          account_number: '',
          balance: '',
          interest_rate: '',
          tax_status: 'taxable',
          notes: '',
          status: 'active'
        });
        fetchAccounts();
      } else {
        toast.error(response.data.error || 'Failed to add account');
      }
    } catch (error) {
      console.error('Error adding account:', error);
      if (error.response?.data?.details && Array.isArray(error.response.data.details)) {
        const errorMessages = error.response.data.details.map(detail => detail.message).join(', ');
        toast.error(`Validation failed: ${errorMessages}`);
      } else {
        toast.error(error.response?.data?.error || 'Failed to add account');
      }
    }
  };

  const handleUpdateAccount = async (data) => {
    try {
      const token = localStorage.getItem('accessToken');
      
      // Remove fields that shouldn't be sent to the server
      const { id, user_id, created_at, updated_at } = selectedAccount;
      
      const updateData = {
        ...data,
        balance: data.balance ? parseFloat(data.balance) : 0,
        interest_rate: data.interest_rate ? parseFloat(data.interest_rate) : null,
        account_number: data.account_number ?? selectedAccount.account_number ?? null,
        tax_status: data.tax_status ?? selectedAccount.tax_status ?? 'taxable'
      };
      
      console.log('🔍 Updating account with data:', updateData);
      
      const response = await axios.put(`/api/accounts/${id}`, updateData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data.success) {
        toast.success('Account updated successfully!');
        setShowEditAccount(false);
        setSelectedAccount(null);
        resetEditAccount();
        fetchAccounts();
      } else {
        toast.error(response.data.error || 'Failed to update account');
      }
    } catch (error) {
      console.error('Error updating account:', error);
      if (error.response?.data?.details && Array.isArray(error.response.data.details)) {
        const errorMessages = error.response.data.details.map(detail => detail.message).join(', ');
        toast.error(`Validation failed: ${errorMessages}`);
      } else if (error.response?.data?.error) {
        toast.error(error.response.data.error);
      } else {
        toast.error('Failed to update account');
      }
    }
  };

  const handleDeleteAccount = async (accountId) => {
    if (window.confirm('Are you sure you want to delete this account?')) {
      try {
        const token = localStorage.getItem('accessToken');
        const response = await axios.delete(`/api/accounts/${accountId}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.data.success) {
          toast.success('Account deleted successfully!');
          fetchAccounts();
        } else {
          toast.error(response.data.error || 'Failed to delete account');
        }
          } catch (error) {
      console.error('Error deleting account:', error);
      toast.error('Failed to delete account');
    }
    }
  };

  const getAccountTypeIcon = (type) => {
    switch (type) {
      case 'checking': return <CreditCard className="w-5 h-5" />;
      case 'savings': return <PiggyBank className="w-5 h-5" />;
      case 'roth_ira': return <Shield className="w-5 h-5" />;
      case 'traditional_ira': return <Target className="w-5 h-5" />;
      case '401k': return <Building2 className="w-5 h-5" />;
      case 'investment': return <TrendingUp className="w-5 h-5" />;
      case 'hsa': return <Wallet className="w-5 h-5" />;
      default: return <DollarSign className="w-5 h-5" />;
    }
  };

  const getAccountTypeColor = (type) => {
    switch (type) {
      case 'checking': return 'bg-blue-100 text-blue-800';
      case 'savings': return 'bg-green-100 text-green-800';
      case 'roth_ira': return 'bg-purple-100 text-purple-800';
      case 'traditional_ira': return 'bg-indigo-100 text-indigo-800';
      case '401k': return 'bg-orange-100 text-orange-800';
      case 'investment': return 'bg-yellow-100 text-yellow-800';
      case 'hsa': return 'bg-teal-100 text-teal-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTaxStatusColor = (status) => {
    switch (status) {
      case 'taxable': return 'bg-red-100 text-red-800';
      case 'tax_deferred': return 'bg-yellow-100 text-yellow-800';
      case 'tax_free': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const filteredAccounts = activeTab === 'all' 
    ? accounts 
    : accounts.filter(account => account.type === activeTab);

  const totalBalance = accounts.reduce((sum, account) => sum + (parseFloat(account.balance) || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Account Manager</h1>
        <p className="text-gray-600">Manage your bank accounts, savings, IRAs, and investment accounts</p>
      </div>
      <Disclaimer variant="compact" />

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <DollarSign className="w-6 h-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Balance</p>
              <p className="text-2xl font-bold text-gray-900">${totalBalance.toLocaleString()}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <PiggyBank className="w-6 h-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Accounts</p>
              <p className="text-2xl font-bold text-gray-900">{accounts.length}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Shield className="w-6 h-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Retirement Accounts</p>
              <p className="text-2xl font-bold text-gray-900">
                {accounts.filter(acc => ['roth_ira', 'traditional_ira', '401k'].includes(acc.type)).length}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <TrendingUp className="w-6 h-6 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Investment Accounts</p>
              <p className="text-2xl font-bold text-gray-900">
                {accounts.filter(acc => acc.type === 'investment').length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="mb-6">
        <nav className="flex space-x-8">
          <button
            onClick={() => setActiveTab('all')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'all'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <PieChart className="inline-block w-4 h-4 mr-2" />
            All Accounts ({accounts.length})
          </button>
          <button
            onClick={() => setActiveTab('checking')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'checking'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <CreditCard className="inline-block w-4 h-4 mr-2" />
            Checking
          </button>
          <button
            onClick={() => setActiveTab('savings')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'savings'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <PiggyBank className="inline-block w-4 h-4 mr-2" />
            Savings
          </button>
          <button
            onClick={() => setActiveTab('roth_ira')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'roth_ira'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Shield className="inline-block w-4 h-4 mr-2" />
            Roth IRA
          </button>
          <button
            onClick={() => setActiveTab('traditional_ira')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'traditional_ira'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Target className="inline-block w-4 h-4 mr-2" />
            Traditional IRA
          </button>
          <button
            onClick={() => setActiveTab('investment')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'investment'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <TrendingUp className="inline-block w-4 h-4 mr-2" />
            Investment
          </button>
        </nav>
      </div>

      {/* Add Account Button */}
      <div className="mb-6">
        <button
          onClick={() => setShowAddAccount(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Account
        </button>
      </div>

      {/* Accounts Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredAccounts.map((account) => (
          <div key={account.id} className="bg-white rounded-lg shadow-md p-6">
            <div className="flex justify-between items-start mb-4">
              <div className="flex items-center">
                <div className="p-2 bg-gray-100 rounded-lg mr-3">
                  {getAccountTypeIcon(account.type)}
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{account.name}</h3>
                  <p className="text-sm text-gray-500">{account.institution}</p>
                </div>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => {
                    setSelectedAccount(account);
                    setShowEditAccount(true);
                  }}
                  className="text-blue-600 hover:text-blue-800"
                >
                  <Edit className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleDeleteAccount(account.id)}
                  className="text-red-600 hover:text-red-800"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="space-y-3 mb-4">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Balance:</span>
                <span className="text-lg font-semibold text-gray-900">
                  ${parseFloat(account.balance || 0).toLocaleString()}
                </span>
              </div>
              
              {account.interest_rate && (
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Interest Rate:</span>
                  <span className="text-sm font-medium text-gray-900">
                    {parseFloat(account.interest_rate).toFixed(2)}%
                  </span>
                </div>
              )}
              
              {account.account_number && (
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Account #:</span>
                  <span className="text-sm font-medium text-gray-900">
                    ****{account.account_number.slice(-4)}
                  </span>
                </div>
              )}
            </div>

            <div className="flex flex-wrap gap-2 mb-4">
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getAccountTypeColor(account.type)}`}>
                {account.type.replace('_', ' ').toUpperCase()}
              </span>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getTaxStatusColor(account.tax_status)}`}>
                {account.tax_status.replace('_', ' ').toUpperCase()}
              </span>
            </div>

            {account.notes && (
              <p className="text-sm text-gray-600 italic">"{account.notes}"</p>
            )}
          </div>
        ))}
      </div>

      {filteredAccounts.length === 0 && (
        <div className="text-center py-12">
          <PiggyBank className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No accounts found</h3>
          <p className="mt-1 text-sm text-gray-500">
            {activeTab === 'all' 
              ? 'Get started by adding your first account.' 
              : `No ${activeTab.replace('_', ' ')} accounts found.`}
          </p>
        </div>
      )}

      {/* Add Account Modal */}
      {showAddAccount && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Add New Account</h3>
              <form onSubmit={handleSubmitAccount(handleAddAccount)}>
                <div className="space-y-4">
                  <ValidatedInput
                    name="name"
                    label="Account Name"
                    type="text"
                    register={registerAccount}
                    errors={accountErrors}
                    placeholder="Enter account name"
                    icon={CreditCard}
                    required
                    {...registerAccount('name', {
                      required: 'Account name is required',
                      minLength: {
                        value: 2,
                        message: 'Account name must be at least 2 characters'
                      },
                      maxLength: {
                        value: 100,
                        message: 'Account name must be no more than 100 characters'
                      }
                    })}
                  />

                  <ValidatedSelect
                    name="type"
                    label="Account Type"
                    register={registerAccount}
                    errors={accountErrors}
                    placeholder="Select account type"
                    icon={Building}
                    required
                    options={[
                      { value: 'checking', label: 'Checking Account' },
                      { value: 'savings', label: 'Savings Account' },
                      { value: 'roth_ira', label: 'Roth IRA' },
                      { value: 'traditional_ira', label: 'Traditional IRA' },
                      { value: '401k', label: '401(k)' },
                      { value: 'investment', label: 'Investment Account' },
                      { value: 'hsa', label: 'Health Savings Account (HSA)' },
                      { value: 'cd', label: 'Certificate of Deposit (CD)' },
                      { value: 'money_market', label: 'Money Market Account' }
                    ]}
                    {...registerAccount('type', {
                      required: 'Account type is required'
                    })}
                  />

                  <ValidatedInput
                    name="institution"
                    label="Institution"
                    type="text"
                    register={registerAccount}
                    errors={accountErrors}
                    placeholder="Enter institution name"
                    icon={Building}
                    required
                    {...registerAccount('institution', {
                      required: 'Institution is required',
                      minLength: {
                        value: 2,
                        message: 'Institution name must be at least 2 characters'
                      },
                      maxLength: {
                        value: 100,
                        message: 'Institution name must be no more than 100 characters'
                      }
                    })}
                  />

                  <ValidatedInput
                    name="account_number"
                    label="Account Number (Last 4 digits)"
                    type="text"
                    register={registerAccount}
                    errors={accountErrors}
                    placeholder="1234"
                    maxLength={4}
                    {...registerAccount('account_number', {
                      pattern: {
                        value: /^\d{0,4}$/,
                        message: 'Account number must be 4 digits or less'
                      }
                    })}
                  />

                  <ValidatedInput
                    name="balance"
                    label="Current Balance"
                    type="number"
                    register={registerAccount}
                    errors={accountErrors}
                    placeholder="0.00"
                    icon={DollarSign}
                    required
                    step="0.01"
                    {...registerAccount('balance', {
                      required: 'Balance is required',
                      ...validationRules.currency,
                      validate: (value) => {
                        const num = parseFloat(value);
                        if (isNaN(num)) {
                          return 'Please enter a valid number';
                        }
                        return true;
                      }
                    })}
                  />

                  <ValidatedInput
                    name="interest_rate"
                    label="Interest Rate (%)"
                    type="number"
                    register={registerAccount}
                    errors={accountErrors}
                    placeholder="0.00"
                    step="0.01"
                    {...registerAccount('interest_rate', {
                      ...validationRules.percentage,
                      validate: (value) => {
                        if (!value) return true;
                        const num = parseFloat(value);
                        if (isNaN(num) || num < 0 || num > 100) {
                          return 'Interest rate must be between 0 and 100%';
                        }
                        return true;
                      }
                    })}
                  />

                  <ValidatedSelect
                    name="tax_status"
                    label="Tax Status"
                    register={registerAccount}
                    errors={accountErrors}
                    placeholder="Select tax status"
                    required
                    options={[
                      { value: 'taxable', label: 'Taxable' },
                      { value: 'tax_deferred', label: 'Tax-Deferred' },
                      { value: 'tax_free', label: 'Tax-Free' }
                    ]}
                    {...registerAccount('tax_status', {
                      required: 'Tax status is required'
                    })}
                  />

                  <ValidatedTextarea
                    name="notes"
                    label="Notes (Optional)"
                    register={registerAccount}
                    errors={accountErrors}
                    placeholder="Optional notes about this account..."
                    rows={3}
                    {...registerAccount('notes', {
                      ...validationRules.maxLength(500, 'Notes')
                    })}
                  />
                </div>

                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddAccount(false);
                      resetAccount();
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Add Account
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Account Modal */}
      {showEditAccount && selectedAccount && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Edit Account</h3>
              <form onSubmit={handleSubmitEditAccount((data) => handleUpdateAccount(data))}>
                <div className="space-y-4">
                  <ValidatedInput
                    name="name"
                    label="Account Name"
                    type="text"
                    register={registerEditAccount}
                    errors={editAccountErrors}
                    placeholder="Enter account name"
                    icon={CreditCard}
                    required
                    defaultValue={selectedAccount.name}
                    {...registerEditAccount('name', {
                      required: 'Account name is required',
                      minLength: {
                        value: 2,
                        message: 'Account name must be at least 2 characters'
                      },
                      maxLength: {
                        value: 100,
                        message: 'Account name must be no more than 100 characters'
                      }
                    })}
                  />

                  <ValidatedSelect
                    name="type"
                    label="Account Type"
                    register={registerEditAccount}
                    errors={editAccountErrors}
                    placeholder="Select account type"
                    icon={Building}
                    required
                    defaultValue={selectedAccount.type}
                    options={[
                      { value: 'checking', label: 'Checking Account' },
                      { value: 'savings', label: 'Savings Account' },
                      { value: 'roth_ira', label: 'Roth IRA' },
                      { value: 'traditional_ira', label: 'Traditional IRA' },
                      { value: '401k', label: '401(k)' },
                      { value: 'investment', label: 'Investment Account' },
                      { value: 'hsa', label: 'Health Savings Account (HSA)' },
                      { value: 'cd', label: 'Certificate of Deposit (CD)' },
                      { value: 'money_market', label: 'Money Market Account' }
                    ]}
                    {...registerEditAccount('type', {
                      required: 'Account type is required'
                    })}
                  />

                  <ValidatedInput
                    name="institution"
                    label="Institution"
                    type="text"
                    register={registerEditAccount}
                    errors={editAccountErrors}
                    placeholder="Enter institution name"
                    icon={Building}
                    required
                    defaultValue={selectedAccount.institution}
                    {...registerEditAccount('institution', {
                      required: 'Institution is required',
                      minLength: {
                        value: 2,
                        message: 'Institution name must be at least 2 characters'
                      },
                      maxLength: {
                        value: 100,
                        message: 'Institution name must be no more than 100 characters'
                      }
                    })}
                  />

                  <ValidatedInput
                    name="balance"
                    label="Current Balance"
                    type="number"
                    register={registerEditAccount}
                    errors={editAccountErrors}
                    placeholder="0.00"
                    icon={DollarSign}
                    required
                    step="0.01"
                    defaultValue={selectedAccount.balance}
                    {...registerEditAccount('balance', {
                      required: 'Balance is required',
                      ...validationRules.currency,
                      validate: (value) => {
                        const num = parseFloat(value);
                        if (isNaN(num)) {
                          return 'Please enter a valid number';
                        }
                        return true;
                      }
                    })}
                  />

                  <ValidatedInput
                    name="interest_rate"
                    label="Interest Rate (%)"
                    type="number"
                    register={registerEditAccount}
                    errors={editAccountErrors}
                    placeholder="0.00"
                    step="0.01"
                    defaultValue={selectedAccount.interest_rate || ''}
                    {...registerEditAccount('interest_rate', {
                      ...validationRules.percentage,
                      validate: (value) => {
                        if (!value) return true;
                        const num = parseFloat(value);
                        if (isNaN(num) || num < 0 || num > 100) {
                          return 'Interest rate must be between 0 and 100%';
                        }
                        return true;
                      }
                    })}
                  />

                  <ValidatedTextarea
                    name="notes"
                    label="Notes (Optional)"
                    register={registerEditAccount}
                    errors={editAccountErrors}
                    placeholder="Optional notes about this account..."
                    rows={3}
                    defaultValue={selectedAccount.notes || ''}
                    {...registerEditAccount('notes', {
                      ...validationRules.maxLength(500, 'Notes')
                    })}
                  />
                </div>

                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    type="button"
                    onClick={() => {
                      setShowEditAccount(false);
                      resetEditAccount();
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Update Account
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountManager;
